import React, { useCallback, useEffect, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  where,
} from '@firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import CountryOptions from '../Components/CountryOptions.js';
import CurrencyOptions from '../Components/CurrencyOptions.js';
import { app } from '../firebase.js';

const INITIAL_BRANCH_FORM = {
  branchName: '',
  Country: 'Kenya',
  Town: '',
  Currency: 'KES',
};

function BranchesComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const [branchesList, setBranchesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalShowUp, setModalShowUp] = useState(false);
  const [branchForm, setBranchForm] = useState(INITIAL_BRANCH_FORM);

  const resetBranchForm = useCallback(() => {
    setBranchForm(INITIAL_BRANCH_FORM);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalShowUp(false);
    resetBranchForm();
  }, [resetBranchForm]);

  const loadBranches = useCallback(async () => {
    setLoading(true);

    try {
      const branchesSnapshot = await getDocs(query(collection(db, 'Branches')));
      const branchRows = await Promise.all(
        branchesSnapshot.docs.map(async (branchDoc) => {
          const branchData = branchDoc.data();
          const branchName = branchData.branchName || '';
          const usersSnapshot = await getDocs(
            query(
              collection(db, 'users'),
              where('userStatus', '==', 'Student'),
              where('classType', '==', branchName)
            )
          );

          const users = usersSnapshot.docs.map((userDoc) => ({
            id: userDoc.id,
            ...userDoc.data(),
          }));

          return {
            id: branchDoc.id,
            ...branchData,
            docID: branchData.docID || branchDoc.id,
            myquerysize: usersSnapshot.size,
            users,
          };
        })
      );

      branchRows.sort((firstBranch, secondBranch) =>
        String(firstBranch.branchName || '').localeCompare(String(secondBranch.branchName || ''))
      );

      setBranchesList(branchRows);
    } catch (error) {
      console.error('Error fetching branches:', error);
      swal('Error', 'Failed to load branches.', 'error');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setBranchesList([]);
        setLoading(false);
        return;
      }

      loadBranches();
    });

    return () => unsubscribe();
  }, [auth, loadBranches]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setBranchForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const submitNewBranch = async (event) => {
    event.preventDefault();

    const branchName = branchForm.branchName.trim();
    const country = branchForm.Country.trim();
    const town = branchForm.Town.trim();
    const currency = branchForm.Currency.trim();

    if (!branchName || !country || !town || !currency) {
      swal('Missing details', 'Please fill in branch name, country, town, and currency.', 'warning');
      return;
    }

    const duplicateBranch = branchesList.some(
      (branch) => String(branch.branchName || '').trim().toLowerCase() === branchName.toLowerCase()
    );

    if (duplicateBranch) {
      swal('Branch exists', 'A branch with that name already exists.', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const newBranchRef = doc(collection(db, 'Branches'));
      await setDoc(newBranchRef, {
        branchName,
        Country: country,
        Town: town,
        Currency: currency,
        docID: newBranchRef.id,
        timeCreated: Timestamp.now(),
      });

      await loadBranches();
      swal('Success!', 'Branch added successfully.', 'success');
      handleCloseModal();
    } catch (error) {
      console.error('Error adding branch:', error);
      swal('Error!', 'Failed to add branch.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  function AddBranchModal(props) {
    return (
      <Modal {...props} aria-labelledby="add-branch-modal-title" centered>
        <Form onSubmit={submitNewBranch}>
          <Modal.Header closeButton>
            <Modal.Title id="add-branch-modal-title">Add a New Branch</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formBranchName">
              <Form.Label>Branch Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter branch name"
                name="branchName"
                value={branchForm.branchName}
                onChange={handleFormChange}
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCountry">
              <Form.Label>Country</Form.Label>
              <Form.Select
                aria-label="Select branch country"
                name="Country"
                value={branchForm.Country}
                onChange={handleFormChange}
                disabled={isSubmitting}
              >
                <CountryOptions />
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formTown">
              <Form.Label>Town</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter town or city"
                name="Town"
                value={branchForm.Town}
                onChange={handleFormChange}
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group className="mb-0" controlId="formCurrency">
              <Form.Label>Currency</Form.Label>
              <Form.Select
                aria-label="Select branch currency"
                name="Currency"
                value={branchForm.Currency}
                onChange={handleFormChange}
                disabled={isSubmitting}
              >
                <CurrencyOptions />
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" className="btn btn-dark" onClick={props.onHide} disabled={isSubmitting}>
              Close
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span> Saving...</span>
                </>
              ) : (
                'Save Branch'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }

  return (
    <div className="teamsComponent">
      <div className="teamsTopBar">
        <h5 className="tHide1">Branches</h5>
        <h5 className="tHide2">Branches</h5>
        <button className="startOnlSessBtn" onClick={() => setModalShowUp(true)}>
          <FontAwesomeIcon icon={faBuilding} className="liveSessionIcon" /> Add a new Branch
        </button>
        <button className="startOnlSessBtn2" onClick={() => setModalShowUp(true)}>
          <FontAwesomeIcon icon={faBuilding} className="liveSessionIcon" /> Add a new Branch
        </button>
      </div>
      <br />

      {loading ? (
        <div className="spinner-container">
          <Spinner animation="border" role="status" />
          <span>Loading...</span>
        </div>
      ) : (
        branchesList.map((branch) => {
          const profileUsers = branch.users.filter((user) => user.profilePic).slice(0, 3);
          const remainingUsersCount = branch.myquerysize - profileUsers.length;

          return (
            <div className="channelItem" key={branch.id}>
              <div className="channelItemTop"></div>
              <div className="channelItemCont">
                <h5>{branch.branchName}</h5>
                <p>Located in {branch.Country}, {branch.Town}</p>
                <p>Currency: {branch.Currency || 'Not set'}</p>
                <div className="profilePlaceHolderCont">
                  {profileUsers.map((user) => (
                    <div className="profileChannel" key={user.id}>
                      <img src={user.profilePic} alt="" />
                    </div>
                  ))}
                  {remainingUsersCount > 0 && (
                    <div className="profileChannel2">
                      <span>+{remainingUsersCount}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {!loading && branchesList.length === 0 && (
        <div className="spinner-container">
          <span>No branches added yet.</span>
        </div>
      )}

      <br /> <br />
      <AddBranchModal show={modalShowUp} onHide={handleCloseModal} />
    </div>
  );
}

export default BranchesComp;
